import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import VisitService from "../../../services/api/visit.service";
import { VisitStatisticResponse } from "../../../models/visit";

enum VisitStatisticType {
  Day = 0,
  Week = 1,
  Month = 2,
  Year = 3,
}

const DashboardPage = () => {
  const [type, setType] = useState<VisitStatisticType>(VisitStatisticType.Day);

  const [data, setData] = useState<VisitStatisticResponse[]>([]);
  const [totalUserOnline, setTotalUserOnline] = useState<number>(0);
  const [deviceData, setDeviceData] = useState<
    { device: string; total: number }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const getDeviceStatistics = async () => {
    const res = await VisitService.getDeviceStatistics();

    if (res.success) {
      setDeviceData(res.data || []);
    }
  };

  const getStatistics = async () => {
    setLoading(true);

    const res = await VisitService.getStatistics(type);

    if (res.success) {
      setData(res.data || []);
    }

    setLoading(false);
  };

  const getUserOnline = async () => {
    setLoading(true);
    const res = await VisitService.getUserOnline();

    if (res.success) {
      setTotalUserOnline(res.data ?? 0);
    }

    setLoading(false);
  };

  useEffect(() => {
    getStatistics();
    getUserOnline();
    getDeviceStatistics();
  }, [type]);

  const cardStyle: React.CSSProperties = {
    marginTop: "20px",
    background: "#fff",
    borderRadius: "24px",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  };

  const filterButton = (active: boolean): React.CSSProperties => ({
    height: "42px",
    padding: "0 18px",
    borderRadius: "12px",
    border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
    background: active ? "#2563eb" : "#fff",
    color: active ? "#fff" : "#111827",
    fontWeight: 600,
    cursor: "pointer",
    transition: "0.2s",
  });

  return (
    <div
      style={{
        padding: "12px",
        background: "#f5f7fb",
        minHeight: "100vh",
      }}>
      {/* Header */}
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: "32px",
            fontWeight: 800,
            color: "#111827",
          }}>
          Dashboard
        </h1>

        <p
          style={{
            marginTop: "8px",
            color: "#6b7280",
            fontSize: "15px",
          }}>
          Thống kê lượt truy cập hệ thống
        </p>
      </div>

      {/* Top cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
          gap: "20px",
        }}>
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Tổng lượt truy cập
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#111827",
            }}>
            {data.reduce((sum, item) => sum + item.total, 0)}
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Trung bình
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#2563eb",
            }}>
            {data.length > 0
              ? Math.round(
                  data.reduce((sum, item) => sum + item.total, 0) / data.length,
                )
              : 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Cao nhất
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#16a34a",
            }}>
            {Math.max(...data.map((x) => x.total), 0)}
          </div>
        </div>
      </div>

      {/* <div style={cardStyle}>
        <div
          style={{
            fontSize: "14px",
            color: "#6b7280",
            marginBottom: "10px",
          }}>
          Người dùng online
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <div
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "999px",
              background: "#22c55e",
              boxShadow: "0 0 12px rgba(34,197,94,0.8)",
            }}
          />

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#22c55e",
            }}>
            {totalUserOnline}
          </div>
        </div>

        <div
          style={{
            marginTop: "12px",
            color: "#6b7280",
            fontSize: "13px",
          }}>
          Đang hoạt động realtime
        </div>
      </div> */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
          gap: "20px",
        }}>
        {/* Card Online */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Người dùng online
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
            <div
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "999px",
                background: "#22c55e",
                boxShadow: "0 0 12px rgba(34,197,94,0.8)",
              }}
            />

            <div
              style={{
                fontSize: "34px",
                fontWeight: 800,
                color: "#22c55e",
              }}>
              {totalUserOnline}
            </div>
          </div>

          <div
            style={{
              marginTop: "12px",
              color: "#6b7280",
              fontSize: "13px",
            }}>
            Đang hoạt động realtime
          </div>
        </div>

        {/* Card Device */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "18px",
            }}>
            Thiết bị truy cập
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}>
            {deviceData.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <span>{item.device}</span>

                <strong>{item.total}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={cardStyle}>
        {/* Filter */}
        <div
          style={{
            display: "flex",
            marginTop: "12px",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "12px",
          }}>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 700,
              }}>
              Biểu đồ truy cập
            </h2>

            <p
              style={{
                marginTop: "6px",
                color: "#6b7280",
                fontSize: "14px",
              }}>
              Theo dõi lượng truy cập hệ thống
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}>
            <button
              style={filterButton(type === VisitStatisticType.Day)}
              onClick={() => setType(VisitStatisticType.Day)}>
              Theo ngày
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Week)}
              onClick={() => setType(VisitStatisticType.Week)}>
              Theo tuần
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Month)}
              onClick={() => setType(VisitStatisticType.Month)}>
              Theo tháng
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Year)}
              onClick={() => setType(VisitStatisticType.Year)}>
              Theo năm
            </button>
          </div>
        </div>

        {/* Chart */}
        <div
          style={{
            width: "100%",
            height: "420px",
          }}>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorVisit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />

                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="label" />

                <YAxis />

                <Tooltip />

                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#2563eb"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorVisit)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Bottom charts */}
      <div
        style={{
          marginTop: "24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(400px,1fr))",
          gap: "24px",
        }}>
        {/* Line chart */}
        <div style={cardStyle}>
          <h3
            style={{
              marginBottom: "20px",
            }}>
            Xu hướng truy cập
          </h3>

          <div
            style={{
              width: "100%",
              height: "300px",
            }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="label" />

                <YAxis />

                <Tooltip />

                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#16a34a"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar chart */}
        <div style={cardStyle}>
          <h3
            style={{
              marginBottom: "20px",
            }}>
            So sánh lượt truy cập
          </h3>

          <div
            style={{
              width: "100%",
              height: "300px",
            }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="label" />

                <YAxis />

                <Tooltip />

                <Bar dataKey="total" fill="#f59e0b" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
